import type { CrremDashboardPayload } from "features/crrem-pathway-chart/model/types";

import { Stack } from "@mui/material";
import { LoadingOrEmptyWrapper } from "components/LoadingAndEmptyHandler";
import { useTranslation } from "react-i18next";

import type {
  CrremSettings,
  EnergyAnalysisDimension,
  EnergyAnalysisRow,
  EnergyAnalysisViewModel,
} from "../model/types";

import { ENERGY_ANALYSIS_DIMENSION } from "../model/constants";
import { EnergyAnalysisChartsRow } from "./EnergyAnalysisChartsRow";
import { EnergyAnalysisDataTable } from "./EnergyAnalysisDataTable";
import { EnergyAnalysisInsights } from "./EnergyAnalysisInsights";
import { EnergyAnalysisTabKpis } from "./EnergyAnalysisTabKpis";

type EnergyAnalysisDashboardBodyProps = {
  dimension: EnergyAnalysisDimension;
  isEmpty: boolean;
  isLoading: boolean;
  payload?: CrremDashboardPayload;
  rows: EnergyAnalysisRow[];
  settings?: CrremSettings;
  viewModel: EnergyAnalysisViewModel;
};

export const EnergyAnalysisDashboardBody = ({
  dimension,
  isEmpty,
  isLoading,
  payload,
  rows,
  settings,
  viewModel,
}: EnergyAnalysisDashboardBodyProps) => {
  const { t } = useTranslation();
  const isInsightsOnly =
    dimension === ENERGY_ANALYSIS_DIMENSION.joint || dimension === ENERGY_ANALYSIS_DIMENSION.corrections;
  return (
    <Stack className="w-full flex-col items-stretch gap-4">
      <LoadingOrEmptyWrapper height="24rem" showEmpty={isEmpty} showLoading={isLoading}>
        <>
          <EnergyAnalysisTabKpis
            dimension={dimension}
            payload={payload}
            rows={rows}
            settings={settings}
            viewModel={viewModel}
          />
          {!isInsightsOnly ? (
            <>
              <EnergyAnalysisChartsRow
              categoryTotals={viewModel.categoryTotals}
              dimension={viewModel.dimension}
              mixCells={viewModel.mixCells}
              mixChartTitle={t("BuildingProfile.energyAnalysis.mixChartTitle")}
              mixUseTypes={viewModel.mixUseTypes}
              mixUtilities={viewModel.mixUtilities}
              snapshotValueMax={viewModel.snapshotValueMax}
              totalChartTitle={t("BuildingProfile.energyAnalysis.totalChartTitle")}
              totalValue={viewModel.totalValue}
              trendChartTitle={t("BuildingProfile.energyAnalysis.trendChartTitle")}
              trendSeries={viewModel.trendSeries}
              unit={viewModel.unit}
              years={viewModel.years}
              />
              <EnergyAnalysisInsights
              dimension={dimension}
              payload={payload}
              rows={rows}
              settings={settings}
              viewModel={viewModel}
              />
              <EnergyAnalysisDataTable
              categoryKeys={viewModel.tableCategoryKeys}
              firstColumnLabel={
                viewModel.dimension === ENERGY_ANALYSIS_DIMENSION.mix
                  ? t("BuildingProfile.energyAnalysis.columns.useType")
                  : t("BuildingProfile.energyAnalysis.columns.year")
              }
              rows={viewModel.tableRows}
              valueLabel={viewModel.unit}
              />
            </>
          ) : (
            <EnergyAnalysisInsights
              dimension={dimension}
              payload={payload}
              rows={rows}
              settings={settings}
              viewModel={viewModel}
            />
          )}
        </>
      </LoadingOrEmptyWrapper>
    </Stack>
  );
};
